import type { DeepPartial } from "@src/app/types/bff";
import { BFF_ENDPOINT } from "@src/app/utils/bff";
import type { MetaFunction } from "react-router";

/** meta info for the page */
export type MetaInfo = DeepPartial<{
  title: string;
  description: string;
  canonical: string;
  keywords: string;
  /**
   * @default "index, follow"
   */
  robots: string;

  /**
   * og tags
   * @tutorial https://ogp.me/
   */
  ogInfo: {
    ogTitle: string;
    ogDescription: string;
    ogType: string;
    ogUrl: string;
    ogImage: string;

    twitterCard: string;
    twitterTitle: string;
    twitterDescription: string;
    twitterImage: string;
    twitterUrl: string;
  };
}>;

const defaultMetaInfo: MetaInfo = {
  title: "红蚂蚁 - 最新消息，言论，意见和生活新闻",
  description:
    "红蚂蚁 - 新加坡，中国，亚洲及世界各地的最新消息，生活新闻，评论，意见和其他新闻, 尽在红蚂蚁。",
  canonical: "https://www.redants.sg",
  keywords:
    "红蚂蚁，新加坡，新闻，评论，综述，假新闻，热词，辟谣，言论，文化，生活，观点，东南亚，狮城，交流，海外",
  robots: "index, follow",

  ogInfo: {
    ogTitle: "",
    ogDescription: "",
    ogType: "website",
    ogUrl: "",
    ogImage: `${BFF_ENDPOINT}/icons/redants-logo.jpg`,

    twitterCard: "summary_large_image",
    twitterTitle: "",
    twitterDescription: "",
    twitterImage: "",
    twitterUrl: "",
  },
};

/**
 * @see https://docs.google.com/spreadsheets/d/1JENgXBy5uUzs-1M6LAjYCfFUGp6qBzWmx0qa4zB8PQg/edit?gid=751659689#gid=751659689
 */
export const createMetaTags = (props?: MetaInfo): ReturnType<MetaFunction> => {
  const _metaInfo = { ...defaultMetaInfo, ...props };
  const _ogInfo = { ...defaultMetaInfo.ogInfo, ...props?.ogInfo };

  return [
    { title: _metaInfo.title },
    { name: "description", content: _metaInfo.description },
    { name: "robots", content: _metaInfo.robots },
    { name: "keywords", content: _metaInfo.keywords },
    { tagName: "link", rel: "canonical", href: _metaInfo.canonical },

    // Open Graph tags
    { property: "og:title", content: _ogInfo.ogTitle || _metaInfo.title },
    {
      property: "og:description",
      content: _ogInfo.ogDescription || _metaInfo.description,
    },
    { property: "og:type", content: _ogInfo.ogType },
    { property: "og:image", content: _ogInfo.ogImage },
    { property: "og:url", content: _ogInfo.ogUrl || _metaInfo.canonical },

    // Twitter tags
    { property: "twitter:card", content: _ogInfo.twitterCard },
    {
      property: "twitter:title",
      content: _ogInfo.twitterTitle || _ogInfo.ogTitle || _metaInfo.title,
    },
    {
      property: "twitter:description",
      content:
        _ogInfo.twitterDescription ||
        _ogInfo.ogDescription ||
        _metaInfo.description,
    },
    {
      property: "twitter:image",
      content: _ogInfo.twitterImage || _ogInfo.ogImage,
    },
    {
      property: "twitter:url",
      content: _ogInfo.twitterUrl || _ogInfo.ogUrl || _metaInfo.canonical,
    },
  ];
};
